import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.quest';

interface ArticleLayoutProps {
  article: Article;
}

function buildArticleSchema(article: Article): Record<string, unknown> {
  const articleUrl = `${siteUrl}/articolo/${article.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spread',
      url: siteUrl,
    },
    url: articleUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    inLanguage: 'it',
    isAccessibleForFree: true,
  };
}

function buildBreadcrumbSchema(article: Article): Record<string, unknown> {
  const articleUrl = `${siteUrl}/articolo/${article.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Articoli',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title.split(':')[0],
        item: articleUrl,
      },
    ],
  };
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderContent = () => {
    return article.content.map((block, index) => {
      if (block.type === 'heading') {
        return (
          <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-12 mb-4">
            <a id={`section-${index}`} className="no-underline">{block.text}</a>
          </h2>
        );
      }
      if (block.type === 'paragraph') {
        const isIntro = index === 0;
        const isLastTwo = index >= article.content.length - 2;
        if (isIntro) {
          return (
            <p key={index} className="text-sm md:text-base text-foreground-700 leading-[1.8] mb-5">
              {block.text?.includes('Itaobuy Spreadsheet') && block.text?.indexOf('Itaobuy Spreadsheet') === block.text?.indexOf('Itaobuy Spreadsheet') ? (
                <>
                  {block.text!.substring(0, block.text!.indexOf('Itaobuy Spreadsheet'))}
                  <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2 transition-colors">
                    Itaobuy Spreadsheet
                  </Link>
                  {block.text!.substring(block.text!.indexOf('Itaobuy Spreadsheet') + 'Itaobuy Spreadsheet'.length)}
                </>
              ) : (
                block.text
              )}
            </p>
          );
        }
        if (isLastTwo) {
          return (
            <p key={index} className="text-sm md:text-base text-foreground-700 leading-[1.8] mb-5">
              {block.text?.includes('homepage') || block.text?.includes('nostra homepage') ? (
                <>
                  {(() => {
                    const idx = block.text!.indexOf('homepage');
                    if (idx === -1) {
                      const idx2 = block.text!.indexOf('nostra homepage');
                      if (idx2 === -1) return block.text;
                      return (
                        <>
                          {block.text!.substring(0, idx2)}
                          <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2 transition-colors">
                            nostra homepage
                          </Link>
                          {block.text!.substring(idx2 + 'nostra homepage'.length)}
                        </>
                      );
                    }
                    const before = block.text!.substring(0, idx);
                    const after = block.text!.substring(idx + 'homepage'.length);
                    const contextBefore = before.split(' ').slice(-2).join(' ');
                    const contextAfter = after.split(' ').slice(0, 2).join(' ');
                    return (
                      <>
                        {before}
                        <Link to="/" className="text-primary-600 font-medium hover:text-primary-700 underline underline-offset-2 transition-colors">
                          homepage
                        </Link>
                        {after}
                      </>
                    );
                  })()}
                </>
              ) : (
                block.text
              )}
            </p>
          );
        }
        return (
          <p key={index} className="text-sm md:text-base text-foreground-700 leading-[1.8] mb-5">
            {block.text}
          </p>
        );
      }
      if (block.type === 'list' && block.items) {
        return (
          <ul key={index} className="list-none space-y-2.5 mb-5 pl-1">
            {block.items.map((item: string, i: number) => (
              <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-400 mt-2 flex-shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        );
      }
      return null;
    });
  };

  return (
    <>
      <JsonLd schema={buildArticleSchema(article)} />
      <JsonLd schema={buildBreadcrumbSchema(article)} />
      <Navbar />
      <main className="pt-24 md:pt-28 pb-14 md:pb-20 bg-background-50">
        <article className="px-4 md:px-6 lg:px-10 max-w-[800px] mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors font-medium">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">Articoli</span>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium truncate max-w-[200px]">{article.title.split(':')[0]}</span>
          </nav>

          <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-sm"></i>
              {article.date}
            </span>
            <span className="text-foreground-300">·</span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-sm"></i>
              {article.author}
            </span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-[44px] font-heading font-bold text-foreground-950 leading-[1.12] mb-8">
            {article.title}
          </h1>

          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-10 font-light">
            {article.metaDescription}
          </p>

          <div className="prose-custom">
            {renderContent()}
          </div>

          <div className="mt-14 p-6 md:p-8 rounded-xl bg-background-100/60">
            <p className="text-sm font-semibold text-foreground-800 mb-3">
              Ti è stata utile questa guida?
            </p>
            <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
              Torna alla homepage per esplorare tutte le categorie di prodotti e trovare quello che cerchi con Itaobuy Spreadsheet.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300"
            >
              <i className="ri-home-4-line"></i>
              Torna alla Homepage
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}